import { useMemo } from 'react';

import { useIntl } from 'react-intl';
import { useHistory, useLocation, useParams, useRouteMatch } from 'react-router-dom';

import { Drawer, message } from 'antd';

import BoxContainer from '@src/components/BoxContainer';
import Title from '@src/components/BoxContainer/components/Title';
import ActionButton from '@src/components/Buttons/ActionButton';
import FilterContainer from '@src/components/FilterContainer';
import { getFullUrl } from '@src/packages/utils';
import { ALLOWED_ROLES } from '@src/packages/utils/access';
import { PATHS } from '@src/packages/workJourney/constants';
import IntlMessages from '@src/util/IntlMessages';

import { formatCoverDrawerTitle } from '../formatCoverDrawerTitle';
import type {
  IWorkJourneyCoverDrawerLocationState,
  IWorkJourneyCoverDrawerParams,
} from '../types';
import { WorkJourneyCoverConflictAlert } from '../WorkJourneyCoverConflictAlert';
import styles from './styles.module.less';

export const WorkJourneyCoverShortageDrawer = () => {
  const intl = useIntl();
  const history = useHistory();
  const { url } = useRouteMatch();
  const { workJourneyConfigId, date } = useParams<IWorkJourneyCoverDrawerParams>();
  const location = useLocation<IWorkJourneyCoverDrawerLocationState | undefined>();
  const { shadow, journeyName, currentMembers, requiredMembers } = location.state ?? {};

  const title = useMemo(
    () => formatCoverDrawerTitle(journeyName, date, intl),
    [journeyName, date, intl],
  );

  const handleClose = () => {
    if (shadow) {
      history.goBack();
      return;
    }
    history.push(getFullUrl(PATHS.WORK_JOURNEY_MANAGEMENT, url));
  };

  const handleAssignUsers = () => {
    message.info(intl.formatMessage({ id: 'workJourney.management.placeholder.comingSoon' }));
  };

  return (
    <Drawer
      open={Boolean(workJourneyConfigId && date)}
      width="100%"
      title={null}
      closable={false}
      footer={null}
      destroyOnClose
      onClose={handleClose}
    >
      <BoxContainer>
        <BoxContainer content fixed bottomBorder>
          <FilterContainer goBack={handleClose} title={<Title.Header value={title} />} />
        </BoxContainer>

        <BoxContainer content>
          <Title.SubTitle
            className="gx-mb-3"
            value={<IntlMessages id="workJourney.management.cover.conflict" />}
          />

          <WorkJourneyCoverConflictAlert
            title={<IntlMessages id="workJourney.management.cover.shortage.alertTitle" />}
            description={intl.formatMessage(
              { id: 'workJourney.management.cover.shortage.alertDescription' },
              {
                current: currentMembers ?? '—',
                required: requiredMembers ?? '—',
              },
            )}
          />

          <div className={styles.assignActions}>
            <ActionButton
              label={intl.formatMessage({ id: 'workJourney.management.cover.shortage.assignUsers' })}
              onClick={handleAssignUsers}
              type="default"
              color="secondary"
              size="medium"
              allowedRoles={ALLOWED_ROLES.ORGANIZATIONS.WORK_JOURNEY.UPDATE}
              disabledScaling
            />
          </div>
        </BoxContainer>
      </BoxContainer>
    </Drawer>
  );
};
